import React from 'react';
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const MembershipContainer = styled.div`
    margin: 1.2rem 1.5rem 0;
    padding: 1.4rem;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 16px;
    backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h3`
    font-size: 0.85rem;
    font-weight: 600;
    color: #c7d2fe;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);

    &:last-child { border-bottom: none; }
`;

const RowLabel = styled.span`
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.45);
`;

const RowValue = styled.span`
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.85);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.6rem;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.7rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;

    background: ${props => props.$active
        ? 'rgba(52, 211, 153, 0.15)'
        : 'rgba(251, 191, 36, 0.15)'};
    color: ${props => props.$active ? '#6ee7b7' : '#fcd34d'};
    border: 1px solid ${props => props.$active
        ? 'rgba(52, 211, 153, 0.25)'
        : 'rgba(251, 191, 36, 0.25)'};
`;

const ActionButton = styled.button`
    padding: 0.45rem 1.2rem;
    border-radius: 10px;
    border: none;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    background: ${props => props.$variant === 'upgrade'
        ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
        : 'rgba(255, 255, 255, 0.08)'};
    color: ${props => props.$variant === 'upgrade'
        ? 'white'
        : 'rgba(255, 255, 255, 0.6)'};
    border: ${props => props.$variant === 'upgrade'
        ? 'none'
        : '1px solid rgba(255, 255, 255, 0.1)'};
    box-shadow: ${props => props.$variant === 'upgrade'
        ? '0 3px 12px rgba(99, 102, 241, 0.3)'
        : 'none'};

    &:hover {
        transform: translateY(-1px);
        background: ${props => props.$variant === 'upgrade'
            ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            : 'rgba(255, 255, 255, 0.12)'};
        color: ${props => props.$variant === 'upgrade' ? 'white' : 'rgba(255,255,255,0.8)'};
    }
`;

const Description = styled.p`
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.4);
    line-height: 1.6;
    margin-top: 0.6rem;
`;

const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const renderQuota = (sent, quota) => {
    const capped = sent > quota ? quota : sent;
    return `${capped} / ${quota}`;
};

const redirectToStripeCheckout = (userId, userEmail) => {
    let locale = localStorage.getItem('i18nextLng');
    window.location.href = `${process.env.REACT_APP_STRIPE_PAYMENT_URL}?client_reference_id=${userId}&prefilled_email=${userEmail}&locale=${locale}`;
};

const SubscriptionStatus = ({ user, onCancelSub }) => {
    const { t } = useTranslation();

    return (
        <MembershipContainer>
            <SectionTitle>{t("SubformMemStatus")}</SectionTitle>

            {user.subscriber ? (
                <>
                    <Row>
                        <RowLabel>Status</RowLabel>
                        <RowValue>
                            <StatusBadge $active={!user.subscriptionCancelled}>
                                {user.subscriptionCancelled ? `${t("SubformSubscriber")} (cancelled)` : t("SubformSubscriber")}
                            </StatusBadge>
                        </RowValue>
                    </Row>
                    <Row>
                        <RowLabel>{t("SubformSince")}</RowLabel>
                        <RowValue>{formatDate(user.subscribedOn)}</RowValue>
                    </Row>
                    {user.subscriptionCancelled && (
                        <Row>
                            <RowLabel>Ends</RowLabel>
                            <RowValue>{formatDate(user.subscriptionPeriodEnd)}</RowValue>
                        </Row>
                    )}
                    {!user.subscriptionCancelled && (
                        <Row>
                            <RowLabel />
                            <RowValue>
                                <ActionButton onClick={onCancelSub}>{t("CancelButton")}</ActionButton>
                            </RowValue>
                        </Row>
                    )}
                </>
            ) : (
                <>
                    <Row>
                        <RowLabel>Status</RowLabel>
                        <RowValue>
                            <StatusBadge $active={false}>{t("SubformFreeTrial")}</StatusBadge>
                            <ActionButton $variant="upgrade" onClick={() => redirectToStripeCheckout(user.id, user.email)}>
                                {t("SubformUpgrade")}
                            </ActionButton>
                        </RowValue>
                    </Row>
                    <Row>
                        <RowLabel>Messages This Month</RowLabel>
                        <RowValue>{renderQuota(user.messagesSent, user.messageQuota)}</RowValue>
                    </Row>
                    {user.quotaResetDate && (
                        <Row>
                            <RowLabel>Resets On</RowLabel>
                            <RowValue>{formatDate(user.quotaResetDate)}</RowValue>
                        </Row>
                    )}
                    <Description>{t("SubformDesc")}</Description>
                </>
            )}
        </MembershipContainer>
    );
};

export default SubscriptionStatus;
